import { captureException, captureMessage } from '@sentry/react';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import { FileAttachment } from 'api/types';
import { setSentryContext } from 'api/utils';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { useCallback, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  onAgentConnected,
  selectChatId,
  selectIntegrationConfig,
  selectLiveChatStatus,
  setLiveChatId,
  setLiveChatStatus,
} from 'reducers/live-chat';
import {
  addWidgetComponents,
  selectConversationId,
  selectIsLiveChatMode,
  selectSuncoLiveChatWidgetComponent,
} from 'reducers/workflows';
import ApiService from 'services/ApiService';
import Smooch from 'smooch';
import { useHandleEndLiveChat } from './useHandleEndLiveChat';
import { JwtPayload } from '../types';
import { processReceivedMessage, SuncoMessage } from 'embed/smooch';
import { mergedUnhandledSuncoMessages } from 'reducers/workflows/index';
import { setIsAgentTyping, setWaitTimeMessage } from 'reducers/live-chat/index';
import { SUNCO_WAIT_TIME_NAME, USER_LEFT_CHAT_MSG } from '../consts';

window.Smooch = Smooch;

export const useHandleSuncoInitialization = () => {
  const dispatch = useAppDispatch();
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const chatId = useAppSelector(selectChatId);
  const liveChatStatus = useAppSelector(selectLiveChatStatus);
  const isTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const suncoWidgetComponent = useAppSelector(
    selectSuncoLiveChatWidgetComponent,
  );
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const ftConversationId = useAppSelector(selectConversationId);
  const shouldDestroySmooch = useRef(false);
  const handleEndLiveChat = useHandleEndLiveChat({
    /**
     * We don't want to send "User left the chat" message after Sunco CSAT,
     * because it will change ticket status from "Closed" to "Open".
     */
    shouldSendUserLeftMessage: false,
  });

  const bindEvents = useCallback(
    ({
      ftConversationId,
      integrationId,
    }: {
      ftConversationId: string;
      integrationId: string;
    }) => {
      // bind events
      Smooch.on('ready', () => {
        const zendeskIFrame = document.querySelector<HTMLObjectElement>(
          '#web-messenger-container',
        );
        if (zendeskIFrame) {
          zendeskIFrame.style.display = 'none';
          Smooch?.close();
        }
      });
      // AGENT
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Smooch.on('message:received', function (message: any) {
        // Received a message that is for updating the estimated wait time only
        if (message.displayName === SUNCO_WAIT_TIME_NAME) {
          dispatch(setWaitTimeMessage(message.text));
          return;
        }

        if (message.source.type !== 'forethought') {
          dispatch(
            onAgentConnected({
              agentIcon: message.avatarUrl,
              agentName: message.displayName,
            }),
          );
        }

        const widgetComp = processReceivedMessage(message);
        if (widgetComp) {
          dispatch(addWidgetComponents([widgetComp]));
        } else if (message.type !== 'text') {
          // report to sentry. Unsupported message type and will not be rendered
          captureMessage('Unsupported message type Sunco Live Chat', {
            extra: {
              conversationId: ftConversationId,
              integrationId: integrationId,
              message: message,
              origin: ApiService.headers['solve-origin'],
            },
            level: 'info',
          });
        }

        if (
          message.type === 'text' &&
          message?.metadata?.type === 'csat' &&
          !message.actions
        ) {
          // Automatically end the chat after 5 seconds after the "Thanks for
          // your feedback" CSAT message:
          setTimeout(handleEndLiveChat, 5000);
        }
      });
      // USER
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Smooch.on('message:sent', function (message: any) {
        if (message.type === 'text') {
          // Don't add user message for CSAT to avoid duplicate messages:
          if (message.payload) {
            return;
          }

          // User left chat message, ignore
          if (message.text === USER_LEFT_CHAT_MSG) {
            return null;
          }

          dispatch(
            addWidgetComponents([
              {
                component_fields: { message: '' },
                component_type: 'text_message',
                isInAgentConvo: true,
                isSent: true,
                query: message.text,
                timestamp: message.received,
              },
            ]),
          );
        } else if (message.type === 'image' || message.type === 'file') {
          const attachments: FileAttachment[] = [
            {
              content_type: message.mediaType ?? '',
              filesize: message.mediaSize ?? 0,
              name: message.altText ?? '',
              url: message.mediaUrl ?? '',
            },
          ];
          dispatch(
            addWidgetComponents([
              {
                component_fields: {
                  attachments: attachments,
                  message: '',
                },
                component_type: 'text_message',
                isInAgentConvo: true,
                isSent: true,
                timestamp: message.received,
              },
            ]),
          );
        }
      });

      Smooch.on('typing:start', () => {
        dispatch(setIsAgentTyping(true));
      });

      Smooch.on('typing:stop', () => {
        dispatch(setIsAgentTyping(false));
      });
    },
    [dispatch, handleEndLiveChat],
  );

  const setupConversation = useCallback(
    async ({
      ftConversationId,
      integrationId,
      jwt,
    }: {
      ftConversationId: string;
      integrationId: string;
      jwt: string | null;
    }) => {
      // for non primary tab, if chatId is not defined or liveChatStatus is not active
      // we don't want to init smooch and conversation since the needed info are not there yet
      if (!isTabPrimary && (!chatId || liveChatStatus !== 'active')) {
        return;
      }

      // inline function to reduce duplicated code
      const sendHandoffTrackingEvent = (
        ftConversationId: string,
        success: boolean,
        error?: string,
      ) => {
        dispatch(
          sendTrackingEvent({
            conversation_id: ftConversationId,
            error,
            event_type: 'widget-customer-handoff',

            // `handoffData` subobject is deprecated, keeping it for backward
            // compatibility:
            handoffData: {
              conversation_id: ftConversationId,
              integration: 'sunco-live-chat',
              success: success,
            },

            integration: 'sunco-live-chat',
            jwt: jwt,
            success,
          }),
        );
      };

      try {
        // init Smooch
        bindEvents({ ftConversationId, integrationId });
        await Smooch.init({ integrationId: integrationId });
        shouldDestroySmooch.current = true;

        // Don't create conversations in this case,as that could cause multiple conversations to be created
        if (liveChatStatus === 'inactive') {
          return;
        }

        // Login Smooch
        if (jwt) {
          try {
            // Decode jwt and extract External ID
            const external_id = jwtDecode<JwtPayload>(jwt)?.external_id || '';
            await Smooch.login(external_id, jwt);
          } catch (error) {
            dispatch(
              sendTrackingEvent({
                event_type: 'widget-handoff-auth-failure',
                integration: 'sunco-live-chat',
              }),
            );
          }
        }

        // init conversation
        if (
          chatId &&
          (liveChatStatus === 'active' || liveChatStatus === 'pending')
        ) {
          await Smooch.loadConversation(chatId);

          // merge the messages into redux widgetComponents
          const conversation = Smooch.getConversations()[0];
          if (conversation) {
            dispatch(
              mergedUnhandledSuncoMessages(
                conversation.messages as SuncoMessage[],
              ),
            );

            // check if the current state is connected
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const agentMessage: any | undefined = conversation.messages.find(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (message: any) =>
                message.source.type !== 'forethought' &&
                message.source.type !== 'web' &&
                message.displayName !== SUNCO_WAIT_TIME_NAME,
            );
            if (agentMessage) {
              dispatch(
                onAgentConnected({
                  agentIcon: agentMessage.avatarUrl,
                  agentName: agentMessage.displayName,
                }),
              );
            }
          }
        } else if (isTabPrimary && liveChatStatus !== 'pending') {
          try {
            await Smooch.createConversation({
              description: 'description',
              displayName: 'displayName',
              metadata: {
                ft_conversation_id: ftConversationId,
                isFirstTimeCustomer: true,
              },
            });

            const smoochConversations = Smooch.getConversations();
            if (
              smoochConversations.length === 0 ||
              !smoochConversations[0]?.id
            ) {
              throw Error(
                'No smooch conversation found after creating the conversation',
              );
            }

            dispatch(setLiveChatStatus('pending'));
            dispatch(setLiveChatId(smoochConversations[0]?.id));
            sendHandoffTrackingEvent(ftConversationId, true);
          } catch {
            // duplicate conversations error could be thrown if the useEffect gets triggered multiple times
            // quickly and try to create more than one convo. We should log and ignore.
            console.error('Smooch tried to create duplicated conversation');
          }
        }
      } catch (error) {
        sendHandoffTrackingEvent(
          ftConversationId,
          false,
          (error instanceof Error && error.message) ||
            'Unknown SunCo Live Chat handoff error',
        );

        // report to sentry
        setSentryContext({
          errorMessage: 'Failed to handoff for Sunco Live Chat',
          origin: ApiService.headers['solve-origin'],
          tags: [
            {
              key: 'conversationId',
              value: ftConversationId,
            },
            {
              key: 'integrationId',
              value: integrationId,
            },
          ],
        });
        captureException(error);
      }
    },
    [dispatch, isTabPrimary, chatId, bindEvents, liveChatStatus],
  );

  useEffect(() => {
    let setupConversationTimeout: NodeJS.Timeout;
    if (
      suncoWidgetComponent &&
      integrationId &&
      ftConversationId &&
      isLiveChatMode
    ) {
      setupConversationTimeout = setTimeout(() => {
        setupConversation({
          ftConversationId,
          integrationId,
          jwt: suncoWidgetComponent?.component_fields.jwt ?? null,
        });
      }, 500);
    }

    return () => {
      clearTimeout(setupConversationTimeout);

      if (shouldDestroySmooch.current) {
        Smooch.destroy();
        shouldDestroySmooch.current = false;
      }
    };
  }, [
    dispatch,
    integrationId,
    ftConversationId,
    isLiveChatMode,
    setupConversation,
    suncoWidgetComponent,
  ]);
};
